
import React, { useState, useEffect, useCallback } from 'react';
import { Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

export interface PersistentFavoriteButtonProps {
  itemId: string;
  onToggleFavorite: (itemId: string) => Promise<void>;
  className?: string;
  isFavorite_prop: boolean; // Now mandatory
  favoriteCount_prop: number; // Now mandatory
  forceWhiteText?: boolean; // For image overlays where text needs to stay white
}

export const PersistentFavoriteButton: React.FC<PersistentFavoriteButtonProps> = ({
  itemId,
  onToggleFavorite,
  className,
  isFavorite_prop,
  favoriteCount_prop,
  forceWhiteText = false,
}) => {
  // Local state for immediate UI feedback (optimistic updates)
  const [localIsFavorite, setLocalIsFavorite] = useState(isFavorite_prop);
  const [localFavoriteCount, setLocalFavoriteCount] = useState(favoriteCount_prop);
  const [justToggled, setJustToggled] = useState(false);
  const [isTogglePending, setIsTogglePending] = useState(false);

  // Sync with props when they change (from React Query cache updates)
  useEffect(() => {
    setLocalIsFavorite(isFavorite_prop);
  }, [isFavorite_prop]);

  useEffect(() => {
    setLocalFavoriteCount(favoriteCount_prop);
  }, [favoriteCount_prop]);

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!itemId || isTogglePending) return;

    setIsTogglePending(true);
    setJustToggled(true);
    
    // Immediate optimistic update for instant UI feedback
    const newIsFavorite = !localIsFavorite;
    const newCount = localIsFavorite ? localFavoriteCount - 1 : localFavoriteCount + 1;
    
    setLocalIsFavorite(newIsFavorite);
    setLocalFavoriteCount(newCount);

    try {
      await onToggleFavorite(itemId);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // Revert optimistic update on error
      setLocalIsFavorite(localIsFavorite);
      setLocalFavoriteCount(localFavoriteCount);
    }

    // Reset animation trigger after delay
    setTimeout(() => setJustToggled(false), 600);
    setIsTogglePending(false);
  };

  return (
    <div className={`inline-flex items-center ${className || ''}`} data-test-id="favorite-button-container">
      <motion.button
        onClick={handleToggleFavorite}
        disabled={isTogglePending}
        className={cn(
          "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full",
          "bg-white/20 dark:bg-black/20",
          "border border-white/20 dark:border-transparent",
          "shadow-lg backdrop-blur-lg",
          "transition-all duration-300",
          isTogglePending && "opacity-70"
        )}
        whileHover={!isTogglePending ? {
          scale: 1.05,
          filter: 'brightness(0.95)',
          transition: { type: "spring", stiffness: 400, damping: 25 }
        } : {}}
        whileTap={!isTogglePending ? { scale: 0.95 } : {}}
        data-test-id="favorite-button"
      >
        <motion.div
          className="flex items-center justify-center relative"
          animate={justToggled ? {
            scale: [1, 1.4, 1.1, 1],
            rotate: [0, -10, 10, 0],
            transition: { 
              duration: 0.6,
              ease: [0.19, 1, 0.22, 1]
            }
          } : {}}
        >
          <div className="relative">
            <AnimatePresence mode="wait">
              {localIsFavorite ? (
                <motion.div
                  key="heart-filled"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "circOut" }}
                >
                  <Heart
                    size={14}
                    className={cn(
                      "text-red-500 fill-red-500",
                      justToggled && "animate-pulse-once"
                    )}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="heart-outline"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "circOut" }}
                >
                  <Heart size={14} className={cn(
                    forceWhiteText 
                      ? "text-white" 
                      : "text-gray-700 dark:text-white"
                  )} />
                </motion.div>
              )}
            </AnimatePresence>
            {/* Pulsing ring effect when toggled to favorite */}
            {justToggled && localIsFavorite && (
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-red-500/70"
                initial={{ scale: 0.8, opacity: 0.7 }}
                animate={{ scale: 1.8, opacity: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />
            )}
          </div>
        </motion.div>
        
        <motion.span 
          className={cn(
            "text-sm font-medium",
            forceWhiteText 
              ? "text-white" 
              : "text-gray-900 dark:text-white"
          )}
          key={localFavoriteCount} // Ensures animation on count change
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
        >
          {localFavoriteCount}
        </motion.span>
      </motion.button>
    </div>
  );
};

export default PersistentFavoriteButton;
